import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startGoogleLogin, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const formatDetail = (d) => {
  if (!d) return "Sign in failed. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

export const Login = () => {
  const { user, loading, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to={user.role === "admin" ? "/boxbquote" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Enter email and password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name || data.email}`);
      // Navigate handled by <Navigate> above once user state updates
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (detail) msg = formatDetail(detail);
      else if (status === 404) msg = "Login is not available on this server yet. Please redeploy or use the preview URL.";
      else if (status === 500 || status === 503) msg = "Login service is temporarily unavailable. Please try again in a moment.";
      else if (!err?.response) msg = "Cannot reach the server. Check your internet connection.";
      else msg = "Sign in failed. Please try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/7464469/pexels-photo-7464469.jpeg?auto=compress&cs=tinysrgb&w=1400"
          alt="BoxB Solutions customer carrying moving boxes"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/85 via-[#0A192F]/50 to-[#2563EB]/40" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to BoxB
          </Link>
          <div>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-white/80">Moving Made Simple.</p>
            <h1 className="mt-3 font-display font-bold text-4xl leading-tight max-w-sm">
              Sign in to manage your enquiries &amp; bookings.
            </h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 rounded-2xl bg-[#0A192F] text-white font-display font-extrabold items-center justify-center text-xl">B</span>
            <span className="font-display font-bold text-[#0A192F] text-lg tracking-tight">BoxB<span className="text-[#2563EB]">.</span></span>
          </div>
          <h2 className="mt-10 font-display font-semibold text-3xl text-[#0A192F] tracking-tight">Welcome back</h2>
          <p className="mt-2 text-slate-600">Sign in with your email or Google account.</p>

          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="password-login-form">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@example.com"
                     className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="••••••••"
                     className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0A192F] outline-none focus:border-[#0A192F]" />
            </div>
            <button type="submit" disabled={busy} data-testid="login-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white px-6 py-3.5 font-semibold hover:bg-[#0F172A] disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <span className="h-px flex-1 bg-slate-200" />
            <span>or</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            data-testid="google-signin-btn"
            onClick={() => startGoogleLogin("/dashboard")}
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-4 font-semibold text-[#0A192F] hover:bg-slate-50 transition-all hover:shadow-md"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <p className="mt-6 text-xs text-slate-500 leading-relaxed">
            By continuing, you agree to BoxB Solutions&apos; Terms &amp; Privacy Policy.
          </p>

          <Link to="/#contact" data-testid="login-quote-link"
                className="mt-8 block text-center rounded-full bg-[#F8FAFC] border border-slate-200 px-6 py-3.5 font-semibold text-[#0A192F] hover:bg-slate-100 transition-all">
            Just want a quote? Submit an enquiry
          </Link>
        </div>
      </div>
    </main>
  );
};
